import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, formatMicros, Job, JobDetail } from "../api/client";

function ErrorNote({error}: {error: unknown}) {
  if (!error) return null;
  return <p role="alert" className="rounded border border-destructive p-3 text-sm">{error instanceof ApiError ? error.messageHr : "Radnja nije uspjela. Osvježi popis i pokušaj ponovno."}</p>;
}

const STATUS_HR: Record<string, string> = {
  queued: "u redu",
  running: "izvršava se",
  awaiting_approval: "čeka odobrenje",
  awaiting_budget_approval: "čeka budžet",
  succeeded: "uspješno",
  failed: "neuspješno",
  cancelled: "otkazano",
  killed: "zaustavljeno",
  needs_review: "za pregled",
};

const CANCEL_POLL_MS = 2000;
const CANCEL_POLL_MAX = 15;
const TERMINAL_STATUSES = ["succeeded", "failed", "cancelled", "killed"];

function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export function JobsPanel({ projectId, canStart, canCancel, canCancelOwn, currentUserId }: {
  projectId: string; canStart: boolean; canCancel: boolean;
  canCancelOwn: boolean; currentUserId: string;
}) {
  const loadGeneration = useRef<symbol | null>(null);
  const [archived, setArchived] = useState(false);
  const viewKey = `${projectId}:${archived}`;
  const currentView = useRef(viewKey);
  currentView.current = viewKey;
  const detailGeneration = useRef<symbol | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [cancelNoticeFor, setCancelNoticeFor] = useState<string | null>(null);
  const cancelPollToken = useRef<symbol | null>(null);
  const cancelPollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set on unmount: once true, no request is started and nothing is written to
  // state even if a request that was already in flight resolves later.
  const unmounted = useRef(false);

  const stopCancelPolling = useCallback(() => {
    cancelPollToken.current = null;
    if (cancelPollTimer.current !== null) {
      clearTimeout(cancelPollTimer.current);
      cancelPollTimer.current = null;
    }
    if (unmounted.current) return;
    setCancelNoticeFor(null);
  }, []);

  // Returns the list it just loaded so callers can decide from fresh data instead
  // of reading React state that has not re-rendered yet. `null` means the answer
  // was stale/discarded (view, generation or unmount) or the request failed.
  const load = useCallback(async (quiet = false): Promise<Job[] | null> => {
    if (unmounted.current || currentView.current !== viewKey) return null;
    const generation = Symbol("jobs-load");
    loadGeneration.current = generation;
    // A quiet refresh keeps the current list and error on screen; clearing them
    // would make the table flash empty while the background poll runs.
    if (!quiet) {
      if (unmounted.current) return null;
      setError(null); setJobs([]);
    }
    try {
      const result = await api.listJobs(projectId, {archived});
      if (unmounted.current) return null;
      if (currentView.current === viewKey && generation === loadGeneration.current) {
        setJobs(result.items);
        return result.items;
      }
      return null;
    } catch (err) {
      if (unmounted.current) return null;
      if (currentView.current === viewKey && generation === loadGeneration.current) setError(err);
      return null;
    }
  }, [projectId, archived, viewKey]);

  useEffect(() => {
    unmounted.current = false;
    void load();
    return () => { loadGeneration.current = Symbol("jobs-unmounted"); detailGeneration.current = null; };
  }, [load]);

  // Unmount teardown: stop the pending poll, invalidate the chain token and mark
  // the component so no late request or setState can run.
  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      loadGeneration.current = Symbol("jobs-unmounted");
      detailGeneration.current = null;
      cancelPollToken.current = null;
      if (cancelPollTimer.current !== null) {
        clearTimeout(cancelPollTimer.current);
        cancelPollTimer.current = null;
      }
    };
  }, []);

  // Polling for a requested cancellation is tied to the current view: a view
  // change or unmount cancels the pending timer and invalidates the chain.
  useEffect(() => {
    cancelPollToken.current = null;
    if (cancelPollTimer.current !== null) {
      clearTimeout(cancelPollTimer.current);
      cancelPollTimer.current = null;
    }
    if (unmounted.current) return;
    setCancelNoticeFor(null);
    return () => {
      cancelPollToken.current = null;
      if (cancelPollTimer.current !== null) {
        clearTimeout(cancelPollTimer.current);
        cancelPollTimer.current = null;
      }
    };
  }, [viewKey]);

  function startCancelPolling(jobId: string) {
    // One tracked cancellation at a time: a running chain must not be overlapped by
    // a second one, or its token/timer would be clobbered.
    if (unmounted.current || cancelNoticeFor !== null) return;
    const token = Symbol("cancel-poll");
    cancelPollToken.current = token;
    let ticks = 0;
    const ticketView = viewKey;
    const next = async () => {
      cancelPollTimer.current = null;
      if (unmounted.current || cancelPollToken.current !== token
          || currentView.current !== ticketView) return;
      ticks += 1;
      // Decide from the list the request just returned: `jobs` state inside this
      // closure is stale, which used to keep the notice alive forever.
      const latest = await load(true);
      if (unmounted.current || cancelPollToken.current !== token
          || currentView.current !== ticketView) return;
      const job = latest?.find((item) => item.id === jobId);
      if (latest === null || !job || isTerminalStatus(job.status) || ticks >= CANCEL_POLL_MAX) {
        if (cancelPollToken.current === token) stopCancelPolling();
        return;
      }
      cancelPollTimer.current = setTimeout(() => { void next(); }, CANCEL_POLL_MS);
    };
    cancelPollTimer.current = setTimeout(() => { void next(); }, CANCEL_POLL_MS);
  }

  async function startJob() {
    if (unmounted.current) return;
    setBusy(true);
    try {
      await api.startJob(projectId, {
        seed: Math.floor(Math.random() * 10_000),
        leads: [
          { ref: "SYN-0001", company: "Synthetic Hotel", country: "HR",
            industry: "hospitality" },
        ],
        // A fresh key per click: a repeated key would deliberately return the same job.
        idempotency_key: `cc-${Date.now()}-${Math.floor(Math.random() * 10_000)}`,
      });
      await load();
    } catch (err) {
      if (!unmounted.current && currentView.current === viewKey) setError(err);
    } finally {
      if (!unmounted.current) setBusy(false);
    }
  }

  async function cancel(jobId: string) {
    if (unmounted.current) return;
    setBusy(true);
    try {
      await api.cancelJob(projectId, jobId);
      if (unmounted.current || currentView.current !== viewKey) return;
      setCancelNoticeFor(jobId);
      await load(true);
      if (!unmounted.current && currentView.current === viewKey) startCancelPolling(jobId);
    } catch (err) {
      if (!unmounted.current && currentView.current === viewKey) setError(err);
    } finally {
      if (!unmounted.current) setBusy(false);
    }
  }

  async function archive(jobId: string) {
    if (unmounted.current) return;
    setBusy(true); setError(null);
    try {
      await api.archiveJob(projectId, jobId, !archived);
      if (unmounted.current) return;
      setDetail(null); await load();
    }
    catch (err) { if (!unmounted.current && currentView.current === viewKey) setError(err); }
    finally { if (!unmounted.current) setBusy(false); }
  }

  async function openDetail(jobId: string) {
    if (unmounted.current) return;
    setError(null);
    const generation = Symbol("job-detail"); detailGeneration.current = generation;
    try {
      const result = await api.getJob(projectId, jobId);
      if (unmounted.current) return;
      if (currentView.current === viewKey && detailGeneration.current === generation) setDetail(result);
    } catch (err) {
      if (unmounted.current) return;
      if (currentView.current === viewKey && detailGeneration.current === generation) setError(err);
    }
  }

  return (
    <div className="space-y-4">
      <ErrorNote error={error} />
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Pokretanje posla samo ga stavlja u red; vanjski se učinak izvodi isključivo u
          workeru i tek nakon odobrenja.
        </p>
        <button
          type="button"
          onClick={() => void startJob()}
          disabled={!canStart || busy || archived}
          className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
          data-testid="control-start-job"
        >
          Pokreni sintetički posao
        </button>
      </div>

      <div className="flex flex-wrap gap-3 text-sm"><button className="rounded border border-border px-3 py-2" onClick={()=>{setDetail(null);setArchived(a=>!a);}}>{archived ? "← Aktivni popis" : "Arhiva otkazanih poslova"}</button><p className="text-muted-foreground">Arhiviranje uklanja otkazani posao iz radnog popisa. Povijest i troškovi ostaju dostupni.</p></div>
      {cancelNoticeFor && (
        <p data-testid="job-cancel-notice" role="status" className="rounded border border-border p-2 text-sm">Otkazivanje je zatraženo; popis se osvježava sam.</p>
      )}
      <table className="w-full text-left text-sm">
        <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="py-2">Posao</th><th>Status</th><th>Korak</th>
            <th>Trošak</th><th />
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.id} className="border-b border-border/50">
              <td className="py-2 font-mono text-xs">{job.id}</td>
              <td data-testid={`job-status-${job.id}`}>
                {job.cancel_requested_at && !isTerminalStatus(job.status)
                  ? "otkazuje se"
                  : STATUS_HR[job.status] ?? job.status}
              </td>
              <td>{job.current_step_index} / 4</td>
              <td>{formatMicros(job.cost_summary?.actual_micros ?? 0)}</td>
              <td className="space-x-2 py-2 text-right">
                <button type="button" className="underline" onClick={() => void openDetail(job.id)}
                        data-testid={`job-open-${job.id}`}>
                  Detalji
                </button>
                {job.status === "cancelled" && (canCancel || (canCancelOwn && job.created_by === currentUserId)) && <button type="button" disabled={busy} className="underline" onClick={()=>void archive(job.id)}>{archived ? "Vrati iz arhive" : "Arhiviraj"}</button>}
                {/* The API accepts either the administrative right or the own-job
                    right, so the button must be offered on the same basis - otherwise
                    an operator could start a job and never see how to stop it
                    (MB-R1-07). While one cancellation is being tracked every Otkaži
                    is disabled: only one tracking (token/timer) may run at a time. */}
                {(canCancel || (canCancelOwn && job.created_by === currentUserId))
                  && !isTerminalStatus(job.status) && !job.cancel_requested_at && (
                  <button type="button" className="underline"
                          disabled={busy || cancelNoticeFor !== null}
                          onClick={() => void cancel(job.id)}
                          data-testid={`job-cancel-${job.id}`}>
                    Otkaži
                  </button>
                )}
              </td>
            </tr>
          ))}
          {jobs.length === 0 && (
            <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">
              Nema poslova u ovom projektu.
            </td></tr>
          )}
        </tbody>
      </table>

      {detail && (
        <section className="rounded border border-border p-4" data-testid="job-detail">
          <h2 className="font-mono text-sm">{detail.id}</h2>
          <p className="mt-1 text-sm">
            {STATUS_HR[detail.status] ?? detail.status} · korak {detail.current_step_index}
            {detail.needs_review_reason && ` · razlog: ${detail.needs_review_reason}`}
          </p>
          <ol className="mt-3 space-y-1 text-sm">
            {detail.steps.map((step) => (
              <li key={step.id} className="flex justify-between border-b border-border/40 py-1">
                <span>{step.index + 1}. {step.step}
                  <span className="ml-2 text-xs text-muted-foreground">{step.kind}</span></span>
                <span>{STATUS_HR[step.status] ?? step.status}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

